// @flow
import React, { useState } from 'react';
import { Button, Form, FormText, InputGroup, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import classNames from 'classnames';
import { BasicTooltip } from './Tooltips';

/* Password Input */
const PasswordInput = ({ name, placeholder, refCallback, errors, register, className }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <>
            <InputGroup className="mb-0">
                <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder={placeholder}
                    name={name}
                    id={name}
                    as="input"
                    ref={(r) => {
                        if (refCallback) refCallback(r);
                    }}
                    className={className}
                    isInvalid={errors && errors[name] ? true : false}
                    {...(register ? register(name) : {})}
                    autoComplete={name}
                />
                <div
                    className={classNames('input-group-text', 'input-group-password', {
                        'show-password': showPassword,
                    })}
                    data-password={showPassword ? 'true' : 'false'}
                >
                    <span
                        className="password-eye"
                        onClick={() => {
                            setShowPassword(!showPassword);
                        }}
                    ></span>
                </div>
            </InputGroup>
        </>
    );
};

type FormInputProps = {
    label?: string,
    type?: string,
    name?: string,
    placeholder?: string,
    register?: any,
    errors?: any,
    className?: string,
    labelClassName?: string,
    containerClass?: string,
    refCallback?: any,
    children?: any,
    parent?: string,
    subtext?: string,
    tooltip?: string,
    buttonName?: string,
    defaultValue?: string,
    value?: any,
};

const FormInput = (props: FormInputProps): React$Element<React$FragmentType> => {
    const { key: _key, ref: _ref, ...cleanProps } = props;
    const {
        label,
        type,
        name,
        placeholder,
        register,
        errors,
        className,
        labelClassName,
        containerClass,
        refCallback,
        children,
        parent,
        subtext,
        tooltip,
        buttonName,
        defaultValue,
        value,
        ...otherProps
    } = cleanProps;

    // handle input type
    const comp = type === 'textarea' ? 'textarea' : type === 'select' ? 'select' : 'input';
    const inputList = value || [{}];
    const { handleRemoveClick, handleAddClick, handleInputChange, formData, setFormData, hideButtons } = otherProps;
    //console.log(hideButtons);

    return (
        <>
            {type === 'hidden' ? (
                <input type={type} name={name} {...(register ? register(name) : {})} {...otherProps} />
            ) : (
                <>
                    {type === 'password' ? (
                        <>
                            <Form.Group className={containerClass}>
                                {label ? (
                                    <>
                                        {' '}
                                        <Form.Label className={labelClassName}>{label}</Form.Label> {children}{' '}
                                    </>
                                ) : null}
                                <PasswordInput
                                    name={name}
                                    placeholder={placeholder}
                                    refCallback={refCallback}
                                    errors={errors}
                                    register={register}
                                    className={className}
                                />
                                {subtext && <FormText>{subtext}</FormText>}
                                {errors && errors[name] ? (
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                        {errors[name]['message']}
                                    </Form.Control.Feedback>
                                ) : null}
                            </Form.Group>
                        </>
                    ) : type === 'appendable' ? (
                        <>
                            {inputList.map((x, i) => {
                                return (
                                    <div className="form-group" key={i}>
                                        <Form.Group
                                            className="position-relative mb-2"
                                            controlId={`validationTooltipAppendable${i}`}
                                        >
                                            <Form.Label>{label}</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    required={otherProps.required ? true : false}
                                                    key={i}
                                                    name={name}
                                                    type="text"
                                                    placeholder={placeholder}
                                                    defaultValue={defaultValue}
                                                    value={x[name]}
                                                    onChange={(e) =>
                                                        handleInputChange(e, parent, i, formData, setFormData)
                                                    }
                                                />
                                                {inputList.length !== 1 && !hideButtons && (
                                                    <Button
                                                        variant="danger"
                                                        onClick={() =>
                                                            handleRemoveClick(parent, i, formData, setFormData)
                                                        }
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </InputGroup>
                                            {subtext && <FormText>{subtext}</FormText>}
                                            {errors && errors[name] ? (
                                                <Form.Control.Feedback type="invalid" className="d-block">
                                                    {errors[name]['message']}
                                                </Form.Control.Feedback>
                                            ) : null}
                                        </Form.Group>
                                        <div className="btn-box">
                                            {inputList.length - 1 === i && (
                                                <button
                                                    className="btn btn-success mb-2"
                                                    type="button"
                                                    onClick={() => handleAddClick(parent, formData, setFormData)}
                                                >
                                                    {buttonName || 'Add Item'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    ) : (
                        <>
                            {type === 'checkbox' || type === 'radio' ? (
                                <>
                                    <Form.Group className={containerClass}>
                                        <Form.Check
                                            type={type}
                                            label={label}
                                            name={name}
                                            id={name}
                                            ref={(r) => {
                                                if (refCallback) refCallback(r);
                                            }}
                                            className={className}
                                            isInvalid={errors && errors[name] ? true : false}
                                            {...(register ? register(name) : {})}
                                            {...otherProps}
                                        />
                                        {subtext && <FormText>{subtext}</FormText>}
                                        {errors && errors[name] ? (
                                            <Form.Control.Feedback type="invalid">
                                                {errors[name]['message']}
                                            </Form.Control.Feedback>
                                        ) : null}
                                    </Form.Group>
                                </>
                            ) : (
                                <Form.Group className={containerClass} as={otherProps.col}>
                                    <Row>
                                        {label ? (
                                            <Col sm={tooltip ? 10 : 12} lg={tooltip ? 10 : 12}>
                                                <Form.Label className={labelClassName}>{label}</Form.Label>
                                            </Col>
                                        ) : null}
                                        {tooltip ? (
                                            <Col sm={2} lg={2}>
                                                <BasicTooltip body={tooltip} />
                                            </Col>
                                        ) : null}
                                    </Row>
                                    <Form.Control
                                        type={type}
                                        placeholder={placeholder}
                                        name={name}
                                        id={name}
                                        as={comp}
                                        ref={(r) => {
                                            if (refCallback) refCallback(r);
                                        }}
                                        className={className}
                                        isInvalid={errors && errors[name] ? true : false}
                                        {...(register ? register(name) : {})}
                                        {...otherProps}
                                        autoComplete={name}
                                        value={value}
                                    >
                                        {children ? children : null}
                                    </Form.Control>
                                    {subtext && <FormText>{subtext}</FormText>}
                                    {errors && errors[name] ? (
                                        <Form.Control.Feedback type="invalid">
                                            {errors[name]['message']}
                                        </Form.Control.Feedback>
                                    ) : null}
                                </Form.Group>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};

/**
 * Multi-select dropdown
 * @param props
 * @returns {*}
 * @constructor
 */
const MultiSelect = ({ label, options, name, labelClassName, value, errors, control, subtext, ...otherProps }) => {
    const setValue = (v, prev) => {
        if ((v && Array.isArray(v)) || v === null) {
            otherProps.handleChange(v, name, otherProps.formData, otherProps.setFormData);
        }
    };

    // // Handle nested options formatting
    // const flattenOptions = (options) => options.flatMap((group) =>
    //     group.options.map((item) => ({
    //         label: `${group.label} > ${item.label}`,
    //         value: item.value,
    //         group: group.label,
    //     })
    //   )
    // );

    return (
        <Form.Group className="mb-3" controlId={name} as={otherProps.col}>
            {label && <Form.Label className={labelClassName}>{label}</Form.Label>}

            <Select
                isMulti={true}
                options={options}
                className="react-select"
                classNamePrefix="react-select"
                value={value}
                isInvalid={errors && errors[name]}
                onChange={(v, prev) => setValue(v, prev)}
                getOptionLabel={(e) => (
                    <div>
                        <strong>{e.group}</strong>
                        {e.label}
                    </div>
                )}
                formatGroupLabel={(group) => (
                    <div>
                        <strong>{group.label}</strong>
                    </div>
                )}
                {...otherProps}
            ></Select>
            {subtext && <FormText>{subtext}</FormText>}
        </Form.Group>
    );
};

export { FormInput, MultiSelect };
